// Modules
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import get from 'lodash/get';
import compose from 'lodash/flowRight';
import { asyncConnect } from 'redux-connect';
import { connect } from 'react-redux';
import { getLang } from 'redux-pagan';
import browserHistory from 'react-router/lib/browserHistory';
// Components
import NotFoundPage from '../NotFoundPage/NotFoundPage';
import PropertyDetailsContainer from '../../containers/PropertyDetailsContainer/PropertyDetailsContainer';
import PropertyDetailsPlaceholder from '../../components/PropertyDetailsPlaceholder/PropertyDetailsPlaceholder';
import withPageWrapper from '../../containers/PageContainer/PageContainer';
// Redux
import {
  receiveProperty,
  getSimilarProperties
} from '../../redux/modules/propertyDetails';
import { setPinAsReviewed } from '../../redux/modules/reviewedPins';
import { editProperty } from '../../redux/modules/manageProperties';
import { changeCurrency } from '../../redux/modules/currency';
// Helpers
import { shareUrl } from '../../constants';
import appendLocale from '../../helpers/appendLocale';
import multiLanguageTags from '../../helpers/multiLanguageHeadTags';
import { AnalyticsEventService } from '../../services';
import { formatPropertyTitle } from '../../utils/formatSEOData';
import { currencies } from '../../containers/PublishForm/PublishPropertyFormConstants';

class PropertyDetailsPage extends Component {
  componentDidMount() {
    window.document.body.classList.add('overflow');
    const propertySlug = get(this.props, 'params.id', '');
    const propertyData = this.props.propertyDetails.data;

    if (!propertyData) {
      this.loadPropertyData(propertySlug);
    } else {
      this.props.setPinAsReviewed(propertyData.id);
    }
    // hermione event
    AnalyticsEventService.viewProperty(propertySlug);
  }

  componentWillUnmount() {
    window.document.body.classList.remove('overflow');
  }

  translatePropertyType(propertyType) {
    return propertyType ? this.props.translate('propertyTypes', propertyType)() : '';
  }

  handleEdit = () => {
    const id = get(this.props, 'propertyDetails.propertyId');
    const { en: { country_name } } = get(this.props, 'propertyDetails.data.address');
    this.props.editProperty(id);
    setTimeout(() => {
      browserHistory.push(appendLocale(this.props.locale, `/property/edit?id=${id}`));
    });
  };

  loadPropertyData = (id) => {
    this.props.receiveProperty({ id, target: 'properties', token: this.props.userToken });
    this.props.getSimilarProperties({ property_id: id });
  }

  render() {
    const data = get(this.props, 'propertyDetails.data');

    if (!data && !this.props.propertyDetails.error) {
      return (
        <PropertyDetailsPlaceholder
          isMobile={this.props.isMobile}
          isRtl={this.props.isRtl}
          data={this.props.propertyDetails.preloadData || {}}
          translate={this.props.translate('propertyDetails')}
        />
      );
    }

    const { route, locale } = this.props;
    const multiLanguage = multiLanguageTags(route, locale);

    if (!data && this.props.propertyDetails.error) {
      const { status } = this.props.propertyDetails.error;
      if (status === 404 || status === 400) {
        return (
          <div>
            <NotFoundPage
              locale={this.props.locale}
              translate={this.props.translate}
              rootTranslate={this.props.translate}
              isMobile={this.props.isMobile}
            />
          </div>
        );
      }
    }

    const translatedPropertyType = this.translatePropertyType(get(data, 'property_type'));

    const title = formatPropertyTitle(
      this.props.propertyDetails.data,
      translatedPropertyType,
      this.props.translate('propertyDetails'),
      this.props.locale
    );

    return (
      <div>
        <Helmet
          titleTemplate={this.props.translate('title', 'titleTemplate')()}
          title={title}
          link={[
            ...multiLanguage,
            { rel: 'canonical', href: `${shareUrl}${route}` }
          ]}
          meta={[
            { property: 'og:url', content: `${shareUrl}${route}` }
          ]}
        />
        <PropertyDetailsContainer
          className="pageDetails detailsPage"
          isModal={false}
          handleEdit={this.handleEdit}
          routeData={this.props.routeData}
        />
      </div>
    );
  }
}

PropertyDetailsPage.propTypes = {
  locale: PropTypes.string,
  isMobile: PropTypes.bool,
  isRtl: PropTypes.bool,
  translate: PropTypes.func,
  propertyDetails: PropTypes.object,
  receiveProperty: PropTypes.func,
  params: PropTypes.object,
  route: PropTypes.any,
  routeData: PropTypes.object,
  editProperty: PropTypes.func,
  setPinAsReviewed: PropTypes.func,
  getSimilarProperties: PropTypes.func,
  userToken: PropTypes.string
};

const mapStateToProps = ({ lang, propertyDetails, userAuthentication }) => ({
  translate: getLang(lang, 'static'),
  propertyDetails,
  userToken: (userAuthentication.data) ? userAuthentication.data.access_token : null
});

const mapDispatchToProps = {
  receiveProperty,
  editProperty,
  setPinAsReviewed,
  getSimilarProperties
};

export default compose(
  asyncConnect([
    {
      key: 'propertyDetails',
      promise: ({ params, store: { dispatch, getState } }) => {
        const state = getState();
        const token = get(state, 'userAuthentication.accessToken') || get(state, 'userAuthentication.data.access_token');

        return dispatch(receiveProperty({ id: params.id, target: 'properties', token }));
      }
    },
    {
      key: 'similarProperties',
      promise: ({ params, store }) =>
        store.dispatch(getSimilarProperties({ property_id: params.id }))
    },
    {
      key: 'currencyFromQuery',
      promise: ({ location, store }) => {
        const requestedCurrency = get(location, 'query.currency');
        if (Object.values(currencies).includes(requestedCurrency)) {
          store.dispatch(changeCurrency(requestedCurrency));
        }
        return Promise.resolve();
      }
    }
  ]),
  connect(mapStateToProps, mapDispatchToProps)
)(withPageWrapper(PropertyDetailsPage));
